import React from "react";
import styled from "styled-components";
import _ from "lodash";
import { connect } from "formik";
import { compose } from "redux";
import Select from "react-select";
import { FormHelperText, FormControl } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";

const TinyHelper = styled.div`
  color: #999999;
  position: absolute;
  top: -13px;
  z-index: 5;
`;

const TinyHelperWithModifiedMargin = styled.div`
  font-family: 'Work Sans';
  text-transform: uppercase;
  font-size: 12px;
  color: #5F6368;
  position: absolute;
  top: -22px;
  z-index: 5;
`;

const StyledFormHelperText = styled(FormHelperText)`
&&&{
  color: rgb(255, 0, 0);
}`;

const styles = () => ({
  root: {
    width: "100%",
    marginTop: "10px"
  }
});

const selectStyles = {
  container: provided => ({
    ...provided,
    borderBottom: "1px solid #2196F3",
    transition: "border 0.1s",
    "&:hover": {
      cursor: "pointer",
      borderBottom: "1px solid #43A8FF"
    },
    padding: "0"
  }),
  valueContainer: provided => ({
    ...provided,
    padding: "0",
    cursor: "pointer"
  }),
  indicatorSeparator: () => ({
    display: "none"
  }),
  placeholder: () => ({
    color: "#999",
    padding: 0,
    position: "absolute",
    top: "9px"
  }),
  control: provided => ({
    ...provided,
    boxShadow: "none",
    border: "none",
    padding: 0
  }),
  menu: provided => ({
    ...provided,
    zIndex: 6
  }),
  singleValue: provided => ({
    ...provided,
    marginLeft: "8px"
  })
};

const addDisabledStyles = (currentSelectStyles, isDisabled) => {
  if (isDisabled) {
    currentSelectStyles.container = provided => ({
      ...provided,
      background: "none",
      border: "0",
      borderBottom: "0px",
      "&:hover": {
        cursor: "pointer",
        borderBottom: "0px"
      }
    });
    currentSelectStyles.control = provided => ({
      ...provided,
      background: "none",
      border: "0"
    });
    currentSelectStyles.indicatorsContainer = provided => ({
      ...provided,
      display: "none"
    });
  }
};
const addErrorStyles = (currentSelectStyles, error, shouldShowError, required) => {
  if (Boolean(error) && shouldShowError && required) {
    currentSelectStyles.placeholder = () => ({
      color: "red",
      padding: 0,
      position: "absolute",
      top: "9px"
    });
    currentSelectStyles.container = provided => ({
      ...provided,
      borderBottom: "1px solid red",
      transition: "border 0.1s",
      "&:hover": {
        cursor: "pointer",
        borderBottom: "1px solid red"
      },
      padding: "0"
    });
  }
};
const addSelectedOptionStyles = (currentSelectStyles, placeholder) => {
  if (placeholder) {
    currentSelectStyles.placeholder = () => ({
      color: "#000",
      padding: 0,
      position: "absolute",
      top: "9px"
    });
  }
};

const addShortWidthStyles = (currentSelectStyles, shortWidth) => {
  if (shortWidth) {
    currentSelectStyles.container = provided => ({
      ...provided,
      width: "177px"
    });
  }
};

const addDarkerBackground = (currentSelectStyles, enableDarkerBackground) => {
  if (enableDarkerBackground) {
    currentSelectStyles.control = provided => ({
      ...provided,
      backgroundColor: "#F5F3F3",
      border: 0,
      borderRadius: "4px 4px 0 0",
      borderBottom: "1px solid #5F6368"
    });
  }
};

const addLongerWidth = (currentSelectStyles, enableLongerWidth, width) => {
  if (enableLongerWidth) {
    currentSelectStyles.container = provided => ({
      ...provided,
      width,
      marginBottom: "1.5rem",
      borderBottom: "1px solid #2196F3",
      transition: "border 0.1s",
      "&:hover": {
        cursor: "pointer",
        borderBottom: "1px solid #43A8FF"
      },
      padding: "0"
    });
  }
};

const shouldShowError = (fieldName, error, touched, validateOnChange) => Boolean(error) && (_.get(touched, fieldName) || validateOnChange);

const AutoCompleteInput = ({
  label,
  fieldName,
  selectName,
  selectValue,
  options,
  formik,
  classes,
  children,
  hideLabel,
  required,
  validateOnChange,
  className,
  shortWidth = false,
  enableDarkerBackground = false,
  enableModifiedTinyHelperMargin = false,
  enableLongerWidth = false,
  width = 120,
  zIndexZero = "",
  ...other
}) => {
  const { isDisabled } = other;
  if (!selectName) {
    selectName = "label";
  }
  if (!selectValue) {
    selectValue = "id";
  }
  const { values, handleChange, errors, touched } = formik;
  const error = _.get(errors, fieldName);
  const shouldShowErrorVal = shouldShowError(fieldName, error, touched, validateOnChange);
  const currentSelectStyles = { ...selectStyles };
  const placeholder = _.find(options, { [selectValue]: _.get(values, fieldName) });
  addErrorStyles(currentSelectStyles, error, shouldShowErrorVal, required);
  addSelectedOptionStyles(currentSelectStyles, placeholder);
  addDisabledStyles(currentSelectStyles, isDisabled);
  addShortWidthStyles(currentSelectStyles, shortWidth);
  addDarkerBackground(currentSelectStyles, enableDarkerBackground);
  addLongerWidth(currentSelectStyles, enableLongerWidth, `${width}px`);

  const displayTinyHelper = !hideLabel && placeholder;

  return (
    <FormControl id={`dropdownFor${fieldName}`} classes={{ root: classes.root }} required={required}>
      {children}
      {(displayTinyHelper && !enableModifiedTinyHelperMargin) && <TinyHelper className={`shrunken-label ${zIndexZero}`}>{label}</TinyHelper>}
      {(displayTinyHelper && enableModifiedTinyHelperMargin) && <TinyHelperWithModifiedMargin className={`shrunken-label ${zIndexZero}`}>{label}</TinyHelperWithModifiedMargin>}
      <Select
        className={className}
        isSearchable
        styles={currentSelectStyles}
        error={Boolean(error) && _.get(touched, fieldName)}
        placeholder={placeholder ? placeholder[selectName] : label}
        value={options.find(x => x.value === _.get(values, fieldName))}
        menuPlacement="auto"
        onChange={a => {
          handleChange(
            {
              target:
              {
                name: fieldName,
                value: a.value
              }
            }
          );
        }}
        options={options.map(x => ({
          label: x[selectName],
          value: x[selectValue]
        }))}
        {...other}
      />
      {Boolean(error) && shouldShowErrorVal && <StyledFormHelperText>{error}</StyledFormHelperText>}
    </FormControl>
  );
};
export default compose(
  withStyles(styles),
  connect
)(AutoCompleteInput);
